import { useState } from "react"
import type { Dispatch, ReactNode } from "react"

import type { SegmentAnalysisAction, SegmentAnalysisState } from "../hooks/use-segment-analysis"
import type { Segment } from "../types"

import { Bookmark, BookmarkPlus, Search, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { buttonVariants } from "@/components/ui/button-variants"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"

import { FavouritesFilterToggle } from "./favourites-filter-toggle"
import { marketColors } from "../data/mock-segment-analysis-data"

type SegmentAnalysisFiltersProps = {
  allSegments: Segment[]
  dispatch: Dispatch<SegmentAnalysisAction>
  /**
   * Market + Owner chips only, for the Companies/Map views (#3306 #7): the
   * search box, Favourites button, "Clear all" and presets stay
   * portfolio-only since those views carry their own search/legend.
   */
  marketAndOwnerOnly?: boolean
  owners: string[]
  /** Saved filter-preset names (#2069 C1); omit handlers to hide the chips. */
  presets?: string[]
  showFavourites: boolean
  state: SegmentAnalysisState
  onApplyPreset?: (name: string) => void
  onDeletePreset?: (name: string) => void
  onSavePreset?: (name: string) => void
}

export function SegmentAnalysisFilters({
  allSegments,
  dispatch,
  marketAndOwnerOnly = false,
  owners,
  presets,
  showFavourites,
  state,
  onApplyPreset,
  onDeletePreset,
  onSavePreset,
}: SegmentAnalysisFiltersProps) {
  const markets = Object.keys(marketColors)
  const activeFilterCount =
    state.filters.market.length +
    state.filters.owner.length +
    (state.filters.search ? 1 : 0) +
    (state.filters.favouritesOnly ? 1 : 0)

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <FilterGroup label="Market">
          {markets.map((market) => (
            <FilterChip
              key={market}
              active={state.filters.market.includes(market)}
              count={allSegments.filter((segment) => segment.marketName === market).length}
              label={market}
              swatch={marketColors[market]}
              onClick={() => dispatch({ type: "toggleFilter", key: "market", value: market })}
            />
          ))}
        </FilterGroup>
        <FilterGroup label="Owner">
          {owners.map((owner) => (
            <FilterChip
              key={owner}
              active={state.filters.owner.includes(owner)}
              count={allSegments.filter((segment) => segment.owner === owner).length}
              label={owner}
              onClick={() => dispatch({ type: "toggleFilter", key: "owner", value: owner })}
            />
          ))}
        </FilterGroup>
        {marketAndOwnerOnly ? null : (
          <>
            <label className="relative min-w-[220px] flex-1 sm:max-w-xs">
              <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
              <input
                className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-primary/15 h-10 w-full rounded-md border pr-3 pl-9 text-sm shadow-sm transition-[box-shadow,border-color] duration-200 focus-visible:ring-[3px] focus-visible:outline-none"
                placeholder="Search segments..."
                value={state.filters.search}
                onChange={(event) => dispatch({ type: "setSearch", value: event.target.value })}
              />
            </label>
            {showFavourites && (
              <FavouritesFilterToggle
                active={state.filters.favouritesOnly}
                onToggle={() => dispatch({ type: "toggleFavouritesFilter" })}
              />
            )}
            {activeFilterCount > 0 && (
              <Button
                className="h-10"
                variant="ghost"
                onClick={() => dispatch({ type: "clearAllFilters" })}
              >
                <X className="size-4" />
                Clear all filters
              </Button>
            )}
            {activeFilterCount > 0 && onSavePreset ? (
              <SavePresetButton onSave={onSavePreset} />
            ) : null}
            {(presets ?? []).map((name) => (
              <span
                key={name}
                className="border-border bg-card text-foreground group inline-flex h-10 items-center gap-1 rounded-md border pl-3 text-sm font-medium shadow-sm"
              >
                <button
                  className="hover:text-primary inline-flex items-center gap-1.5 transition"
                  title={`Apply preset ${name}`}
                  type="button"
                  onClick={() => onApplyPreset?.(name)}
                >
                  <Bookmark className="size-3.5" />
                  {name}
                </button>
                <button
                  aria-label={`Delete preset ${name}`}
                  className="text-muted-foreground/50 hover:text-destructive inline-flex h-full items-center px-2 transition"
                  type="button"
                  onClick={() => onDeletePreset?.(name)}
                >
                  <X className="size-3.5" />
                </button>
              </span>
            ))}
          </>
        )}
      </div>
    </section>
  )
}

/** Inline name-capture for saving the active filter combo as a preset (#2069 C1). */
function SavePresetButton({ onSave }: { onSave: (name: string) => void }) {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState("")
  const submit = () => {
    if (!name.trim()) return
    onSave(name)
    setName("")
    setOpen(false)
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button className="h-10" variant="ghost">
          <BookmarkPlus className="size-4" />
          Save preset
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 space-y-2 p-3">
        <p className="text-xs font-semibold tracking-[0.12em] uppercase">Save current filters</p>
        <input
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          className="border-input bg-background focus-visible:border-primary focus-visible:ring-primary/15 h-9 w-full rounded-md border px-3 text-sm shadow-sm transition-[box-shadow,border-color] duration-200 focus-visible:ring-[3px] focus-visible:outline-none"
          maxLength={40}
          placeholder="Preset name…"
          value={name}
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") submit()
          }}
        />
        <Button className="w-full font-semibold" disabled={!name.trim()} size="sm" onClick={submit}>
          Save
        </Button>
      </PopoverContent>
    </Popover>
  )
}

function FilterGroup({ children, label }: { children: ReactNode; label: string }) {
  // Radix popover (not native <details>) so the menu dismisses on outside click
  // and Escape. Chip toggles inside don't close it — the filters are multi-select.
  return (
    <Popover>
      <PopoverTrigger
        className={buttonVariants({
          variant: "outline",
          className: "h-10 cursor-pointer shadow-sm",
        })}
      >
        {label}
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="grid max-h-80 w-auto min-w-64 gap-1 overflow-x-hidden overflow-y-auto p-2"
        data-testid="filter-group-menu"
      >
        {children}
      </PopoverContent>
    </Popover>
  )
}

function FilterChip({
  active,
  count,
  label,
  onClick,
  swatch,
}: {
  active: boolean
  count: number
  label: string
  onClick: () => void
  swatch?: string
}) {
  return (
    <button
      type="button"
      className={cn(
        "flex w-full items-center justify-between gap-3 rounded-md px-2 py-1.5 text-left text-sm transition",
        active
          ? "bg-primary/10 text-primary"
          : "text-popover-foreground hover:bg-accent hover:text-accent-foreground"
      )}
      onClick={onClick}
    >
      <span className="flex min-w-0 items-center gap-2">
        {swatch && (
          <span className="size-2.5 shrink-0 rounded-full" style={{ background: swatch }} />
        )}
        <span className="truncate">{label}</span>
      </span>
      <span className="text-muted-foreground text-xs">{count}</span>
    </button>
  )
}
